import { useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Input, Area, ImageInput, GalleryField, Toggle } from "./fields";
import { FicheOverlay, ItemCard, CardGrid } from "./Fiche";

// Dedicated team editor: rich member profiles (page /equipe/:slug) with gallery, ordering, on/off.
export const AdminTeam = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(null);
  const v = value || {};
  const members = Array.isArray(v.members) ? v.members : [];
  const set = (k, val) => onChange({ ...v, [k]: val });
  const setM = (i, up) => set("members", members.map((it, x) => (x === i ? { ...it, ...up } : it)));
  const add = () => { set("members", [...members, { name: "", role: "", photo: "", bio: "", profileEnabled: true, gallery: [] }]); setOpen(members.length); };
  const remove = (i) => set("members", members.filter((_, x) => x !== i));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= members.length) return; const c = [...members]; [c[i], c[j]] = [c[j], c[i]]; set("members", c); };

  // Fiche dédiée : profil complet d'UN membre (modification isolée)
  const renderForm = (i) => {
    const m = members[i];
    if (!m) return null;
    return (
      <>
        <Toggle label="Page profil" hint="Affiche le bouton « Voir le profil » qui ouvre la page dédiée du membre." checked={m.profileEnabled !== false} onChange={(val) => setM(i, { profileEnabled: val })} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Nom & prénom" value={m.name} onChange={(val) => setM(i, { name: val })} />
          <Input label="Fonction / rôle" value={m.role} onChange={(val) => setM(i, { role: val })} />
        </div>
        <ImageInput label="Photo (portrait)" value={m.photo} onChange={(val) => setM(i, { photo: val })} />
        <Area label="Présentation (bio)" value={m.bio} onChange={(val) => setM(i, { bio: val })} aiContext="la présentation d'un membre d'une association audiovisuelle" />
        <Area label="Parcours" value={m.parcours} onChange={(val) => setM(i, { parcours: val })} aiContext="le parcours professionnel d'un membre d'une association audiovisuelle" />
        <Area label="Spécialités & compétences (une par ligne)" value={m.competences} onChange={(val) => setM(i, { competences: val })} />
        <Area label="Filmographie (une par ligne)" value={m.filmography} onChange={(val) => setM(i, { filmography: val })} />
        <Area label="Vidéos (une URL par ligne — YouTube/MP4)" value={m.videos} onChange={(val) => setM(i, { videos: val })} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Instagram (URL)" value={m.instagram} onChange={(val) => setM(i, { instagram: val })} />
          <Input label="LinkedIn (URL)" value={m.linkedin} onChange={(val) => setM(i, { linkedin: val })} />
        </div>
        <GalleryField label="Galerie de médias du membre (photos & vidéos)" items={m.gallery || []} settings={m.gallerySettings || {}}
          onItemsChange={(arr) => setM(i, { gallery: arr })} onSettingsChange={(s) => setM(i, { gallerySettings: s })} testid={`team-gallery-${i}`} />
      </>
    );
  };

  return (
    <div className="space-y-5" data-testid="admin-team">
      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Sur-titre" value={v.eyebrow} onChange={(val) => set("eyebrow", val)} />
        <Input label="Titre de la section" value={v.heading} onChange={(val) => set("heading", val)} />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Membres ({members.length})</span>
        <button type="button" onClick={add} data-testid="team-add-btn" className="inline-flex items-center gap-1.5 text-xs text-electric hover:text-white border border-electric/30 rounded-full px-3 py-1.5"><Plus size={13} /> Ajouter un membre</button>
      </div>

      {open !== null && members[open] ? (
        <FicheOverlay testid="team-fiche" title={members[open].name || `Membre #${open + 1}`} subtitle={members[open].role || "Équipe"} onClose={() => setOpen(null)}>
          {renderForm(open)}
        </FicheOverlay>
      ) : (
        <CardGrid testid="team-cards">
          {members.map((m, i) => (
            <ItemCard key={i} testid={`team-member-editor-${i}`}
              image={m.photo} title={m.name || `Membre #${i + 1}`} subtitle={m.role}
              badges={[{ label: m.profileEnabled !== false ? "Profil public" : "Sans page profil", tone: m.profileEnabled !== false ? "text-electric border-electric/40 bg-black/60" : "text-white/50 border-white/15 bg-black/60" }]}
              onOpen={() => setOpen(i)}
              actions={
                <>
                  <button type="button" onClick={() => move(i, -1)} title="Monter" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowUp size={14} /></button>
                  <button type="button" onClick={() => move(i, 1)} title="Descendre" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowDown size={14} /></button>
                  <button type="button" onClick={() => remove(i)} data-testid={`team-remove-${i}`} title="Supprimer" className="p-2 rounded border border-white/10 text-red-400/70 hover:text-red-400 hover:border-red-400/40"><Trash2 size={14} /></button>
                </>
              } />
          ))}
        </CardGrid>
      )}
    </div>
  );
};
